import { useState, useRef } from 'react';

const IMAGE_EXTENSIONS = new Set([
  '.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.ico', '.bmp'
]);

const TEXT_EXTENSIONS = new Set([
  '.txt', '.log', '.md', '.markdown', '.rst',
  '.json', '.json5', '.jsonc',
  '.yml', '.yaml',
  '.toml', '.ini', '.cfg', '.conf', '.config', '.env',
  '.properties', '.prop',
  '.xml', '.html', '.htm', '.xhtml',
  '.css', '.scss', '.sass', '.less',
  '.js', '.mjs', '.cjs', '.jsx',
  '.ts', '.tsx',
  '.java', '.kt', '.kts', '.groovy',
  '.php', '.rb', '.py', '.go', '.rs', '.c', '.cpp', '.h', '.hpp', '.cs',
  '.lua', '.sh', '.bash', '.zsh', '.fish', '.bat', '.cmd', '.ps1',
  '.sql', '.graphql', '.gql',
  '.gitignore', '.gitattributes', '.editorconfig', '.htaccess',
  '.lock', '.list', '.csv', '.tsv',
]);

const getExtension = (path) => {
  if (!path) return '';
  const lower = path.toLowerCase();
  const dot = lower.lastIndexOf('.');
  return dot >= 0 ? lower.slice(dot) : '';
};

const isImageFile = (path) => IMAGE_EXTENSIONS.has(getExtension(path));
const isTextFile = (path) => {
  const ext = getExtension(path);
  if (!ext) return true; // no extension — allow (e.g. Dockerfile, Makefile)
  return TEXT_EXTENSIONS.has(ext);
};

export const useEditors = ({
  api,
  startTransfer,
  refreshLocal,
  refreshRemote,
  localPath,
  remotePath,
  addLog,
  setMessage
}) => {
  const [editor, setEditor] = useState(null);
  const [nbtEditor, setNbtEditor] = useState(null);
  const [editorView, setEditorView] = useState('config');
  const [configEditor, setConfigEditor] = useState(null);
  const [loadingFilePath, setLoadingFilePath] = useState(null);
  const imageUrlRef = useRef(null);

  const nbtEnabled = api?.capabilities?.nbt !== false;

  const cleanupImageUrl = () => {
    if (imageUrlRef.current) {
      if (imageUrlRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrlRef.current);
      }
      imageUrlRef.current = null;
    }
  };

  const openEditor = async (item, isRemote) => {
    if (item.type !== 'file') return;
    if (!api) {
      if (setMessage) setMessage('API bridge unavailable.');
      return;
    }
    const lowerName = (item.name || item.path || '').toLowerCase();

    if (nbtEnabled && (lowerName.endsWith('.dat') || lowerName.endsWith('.mca'))) {
      setLoadingFilePath(item.path);
      await openNbtEditor(item, isRemote);
      setLoadingFilePath(null);
      return;
    }

    if (!isTextFile(item.path) && !isImageFile(item.path)) {
      if (setMessage) setMessage(`Cannot open "${item.name}" — binary files are not supported in the editor.`);
      return;
    }

    if (isRemote && isImageFile(item.path) && api.readRemoteFileAsDataUrl) {
      setLoadingFilePath(item.path);
      const tracker = startTransfer('Open image', item.path);
      try {
        cleanupImageUrl();
        const url = await api.readRemoteFileAsDataUrl(item.path);
        imageUrlRef.current = url;
        setEditor({ path: item.path, isRemote, content: '', isImage: true, imageUrl: url });
        setEditorView('raw');
        if (addLog) addLog(`Opened image ${item.path}`);
        tracker.succeed();
      } catch (err) {
        tracker.fail(err.message);
        if (setMessage) setMessage(err.message);
      } finally {
        setLoadingFilePath(null);
      }
      return;
    }

    setLoadingFilePath(item.path);
    const tracker = startTransfer('Open file', item.path);
    try {
      const content = isRemote ?
      await api.readRemoteFile(item.path) :
      await api.readLocalFile(item.path);

      cleanupImageUrl();
      setEditor({ path: item.path, isRemote, content });

      if (lowerName.endsWith('.json') || lowerName.endsWith('.yml') || lowerName.endsWith('.yaml') || lowerName.endsWith('.properties') || lowerName.endsWith('.toml') || lowerName.endsWith('.xml')) {

        setEditorView('config');
      } else {
        setEditorView('raw');
      }

      if (addLog) addLog(`Opened ${isRemote ? 'remote' : 'local'} file ${item.path}`);
      tracker.succeed();
    } catch (err) {
      tracker.fail(err.message);
      if (setMessage) setMessage(err.message);
    } finally {
      setLoadingFilePath(null);
    }
  };

  const saveEditor = async () => {
    if (!editor || !api) return;
    const tracker = startTransfer('Save file', editor.path, 'action');
    try {
      if (editor.isRemote) {
        await api.writeRemoteFile(editor.path, editor.content);
        await refreshRemote(remotePath);
      } else {
        await api.writeLocalFile(editor.path, editor.content);
        await refreshLocal(localPath);
      }
      tracker.succeed();
    } catch (err) {
      tracker.fail(err.message);
      if (setMessage) setMessage(err.message);
    }
  };

  const openNbtEditor = async (item, isRemote) => {
    if (!api || !nbtEnabled) {
      if (setMessage) setMessage('NBT tools are disabled in web mode.');
      return;
    }
    const tracker = startTransfer('Open NBT', item.path);
    try {









      const lower = item.path.toLowerCase();
      if (lower.endsWith('.mca')) {
        const headers = await api.readRegionHeaders(item.path);
        setNbtEditor({
          path: item.path,
          isRemote,
          mode: 'region',
          chunks: headers,
          selectedChunk: null,
          text: ''
        });
      } else {
        const data = await api.readNbt(item.path);
        setNbtEditor({
          path: item.path,
          isRemote,
          mode: 'file',
          text: JSON.stringify(data, null, 2)
        });
      }
      tracker.succeed();
    } catch (err) {
      tracker.fail(err.message);
      if (setMessage) setMessage(err.message);
    }
  };

  const saveNbtEditor = async () => {
    if (!nbtEditor || !api) return;
    try {
      if (nbtEditor.mode === 'file') {
        const parsed = JSON.parse(nbtEditor.text);
        await api.writeNbt(nbtEditor.path, parsed);
      } else {

        if (nbtEditor.selectedChunk) {
          const parsed = JSON.parse(nbtEditor.text);
          await api.writeRegionChunk(nbtEditor.path, nbtEditor.selectedChunk.x, nbtEditor.selectedChunk.z, parsed);
        }
      }
      if (addLog) addLog(`Saved NBT ${nbtEditor.path}`);

    } catch (err) {
      if (setMessage) setMessage(err.message);
    }
  };


  const loadNbtChunk = async (index) => {
    if (!nbtEditor || nbtEditor.mode !== 'region' || !api) return;
    const chunk = nbtEditor.chunks[index];
    if (!chunk) return;
    try {
      const data = await api.readRegionChunk(nbtEditor.path, chunk.x, chunk.z);
      setNbtEditor((prev) => ({
        ...prev,
        selectedChunk: chunk,
        text: JSON.stringify(data, null, 2)
      }));
    } catch (err) {
      if (setMessage) setMessage(err.message);
    }
  };

  return {
    editor,
    setEditor,
    nbtEditor,
    setNbtEditor,
    editorView,
    setEditorView,
    configEditor,
    setConfigEditor,
    loadingFilePath,
    openEditor,
    saveEditor,
    openNbtEditor,
    saveNbtEditor,
    loadNbtChunk
  };
};
